import React, { useState } from 'react';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import { iosNativePageRoot, iosNativeScreenTitle } from '../styles/iosNativePageCss';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Instagram, Youtube, Search, Smartphone, Stethoscope, Users, CheckCircle } from 'lucide-react';

const PageContainer = styled.div`
  min-height: calc(100vh - 72px);
  background: linear-gradient(180deg, ${theme.colors.background.secondary} 0%, ${theme.colors.background.primary} 100%);
  padding: 2rem 1rem 4rem;
  display: flex;
  align-items: center;
  justify-content: center;

  ${iosNativePageRoot}
`;

const FormCard = styled(Card)`
  max-width: 600px;
  width: 100%;
  animation: slideUp 0.5s ease-out;
`;

const Title = styled.h1`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: ${theme.typography.fontSize['3xl']};

  ${iosNativeScreenTitle}
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.text.primary};
  margin-bottom: 0.5rem;
  text-align: center;

  @media (min-width: ${theme.breakpoints.md}) {
    font-size: ${theme.typography.fontSize['4xl']};
  }
`;

const Subtitle = styled.p`
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.text.secondary};
  text-align: center;
  margin-bottom: 2rem;
  line-height: 1.6;
`;

const OptionsGrid = styled.div`
  display: grid;
  gap: 1rem;
  margin-bottom: 1.5rem;

  @media (min-width: ${theme.breakpoints.sm}) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const OptionButton = styled.button<{ $selected: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 1.5rem 1rem;
  background: ${props => props.$selected ? theme.colors.glow.mint : 'white'};
  border: 2px solid ${props => props.$selected ? theme.colors.primary[500] : theme.colors.border.light};
  border-radius: ${theme.borderRadius.xl};
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: ${theme.typography.fontFamily.primary};
  position: relative;

  &:hover {
    border-color: ${theme.colors.primary[400]};
    transform: translateY(-4px);
    box-shadow: ${theme.shadows.md};
  }

  svg {
    width: 32px;
    height: 32px;
    color: ${props => props.$selected ? theme.colors.primary[600] : theme.colors.text.tertiary};
  }
`;

const OptionLabel = styled.div`
  font-size: ${theme.typography.fontSize.base};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.text.primary};
  text-align: center;
`;

const CheckMark = styled.div<{ $show: boolean }>`
  position: absolute;
  top: 0.5rem;
  right: 0.5rem;
  width: 24px;
  height: 24px;
  background: ${theme.colors.primary[500]};
  border-radius: ${theme.borderRadius.full};
  display: ${props => props.$show ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  color: white;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const TextInputLabel = styled.label`
  display: block;
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  color: ${theme.colors.text.primary};
  margin-bottom: 0.5rem;
`;

const TextInput = styled.textarea`
  width: 100%;
  padding: 0.75rem 1rem;
  border: 2px solid ${theme.colors.border.light};
  border-radius: ${theme.borderRadius.lg};
  font-family: ${theme.typography.fontFamily.primary};
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.text.primary};
  resize: vertical;
  min-height: 80px;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[100]};
  }

  &::placeholder {
    color: ${theme.colors.text.tertiary};
  }
`;

const SuccessCard = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  animation: slideUp 0.5s ease-out;
`;

const SuccessIcon = styled.div`
  width: 80px;
  height: 80px;
  margin: 0 auto 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${theme.colors.mint[100]};
  border-radius: ${theme.borderRadius.full};
  color: ${theme.colors.mint[600]};
  animation: bounce 0.6s ease-in-out;

  svg {
    width: 48px;
    height: 48px;
  }
`;

const SuccessTitle = styled.h2`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.text.primary};
  margin-bottom: 0.75rem;
`;

const SuccessMessage = styled.p`
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.text.secondary};
  line-height: 1.6;
  margin-bottom: 2rem;
`;

const referralOptions = [
  { id: 'instagram', label: 'Instagram', icon: Instagram },
  { id: 'tiktok', label: 'TikTok', icon: Youtube },
  { id: 'friend', label: 'Friend/School Referral', icon: Users },
  { id: 'google', label: 'Google Search', icon: Search },
  { id: 'appstore', label: 'App Store Discovery', icon: Smartphone },
  { id: 'dermatologist', label: 'Dermatologist', icon: Stethoscope },
];

const ReferralPage: React.FC = () => {
  const [selectedSource, setSelectedSource] = useState<string>('');
  const [otherText, setOtherText] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = () => {
    if (!selectedSource) {
      alert('Please select how you heard about us');
      return;
    }

    if (selectedSource === 'other' && !otherText.trim()) {
      alert('Please tell us more');
      return;
    }

    // Store the referral data
    const referralData = {
      source: selectedSource,
      details: selectedSource === 'other' ? otherText : null,
      timestamp: new Date().toISOString(),
    };

    // In production, this would save to Firebase
    console.log('Referral data:', referralData);
    localStorage.setItem('referralSource', JSON.stringify(referralData));

    setSubmitted(true);
  };

  if (submitted) {
    return (
      <PageContainer>
        <FormCard padding="lg">
          <SuccessCard>
            <SuccessIcon>
              <CheckCircle />
            </SuccessIcon>
            <SuccessTitle>Thank you! ✨</SuccessTitle>
            <SuccessMessage>
              Your feedback helps us grow and reach more people on their glow journey. We appreciate you being part of the GlowLens community! 💚
            </SuccessMessage>
            <Button size="lg" onClick={() => window.location.href = '/'}>
              Continue to GlowLens
            </Button>
          </SuccessCard>
        </FormCard>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <FormCard padding="lg">
        <Title>How did you hear about us? 💚</Title>
        <Subtitle>
          We'd love to know how you discovered GlowLens! Your answer helps us reach more people who could benefit from healthier, glowing skin.
        </Subtitle>

        <OptionsGrid>
          {referralOptions.map(option => {
            const Icon = option.icon;
            const isSelected = selectedSource === option.id;

            return (
              <OptionButton
                key={option.id}
                $selected={isSelected}
                onClick={() => setSelectedSource(option.id)}
              >
                <CheckMark $show={isSelected}>
                  <CheckCircle />
                </CheckMark>
                <Icon />
                <OptionLabel>{option.label}</OptionLabel>
              </OptionButton>
            );
          })}
        </OptionsGrid>

        <div style={{ marginBottom: '2rem' }}>
          <OptionButton
            $selected={selectedSource === 'other'}
            onClick={() => setSelectedSource('other')}
            style={{ width: '100%' }}
          >
            <CheckMark $show={selectedSource === 'other'}>
              <CheckCircle />
            </CheckMark>
            <OptionLabel>Other — tell us more!</OptionLabel>
          </OptionButton>

          {selectedSource === 'other' && (
            <div style={{ marginTop: '1rem' }}>
              <TextInputLabel htmlFor="other-text">
                How did you discover GlowLens?
              </TextInputLabel>
              <TextInput
                id="other-text"
                placeholder="Share your story..."
                value={otherText}
                onChange={(e) => setOtherText(e.target.value)}
              />
            </div>
          )}
        </div>

        <Button
          fullWidth
          size="lg"
          onClick={handleSubmit}
          disabled={!selectedSource}
        >
          Submit
        </Button>

        <p style={{ 
          marginTop: '1rem', 
          textAlign: 'center', 
          fontSize: theme.typography.fontSize.xs, 
          color: theme.colors.text.tertiary 
        } as React.CSSProperties}>
          Your response is anonymous and helps us improve 🌱
        </p>
      </FormCard>
    </PageContainer>
  );
};

export default ReferralPage;
